import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getEbookPages } from "../../app/api";
import { useTranslation } from "react-i18next";

const PAGES_PER_CHUNK = 10;

const EbookReader = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const [pages, setPages] = useState<string[]>([]);
  const [currentChunk, setCurrentChunk] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState<"forward" | "backward">("forward");
  const [isMobile, setIsMobile] = useState(false);

  // Pages to show after flip completes (pre-calculated)
  const pendingPageRef = useRef<number | null>(null);
  const pendingChunkRef = useRef<number | null>(null);

  // Responsive breakpoint
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Fetch chunk
  useEffect(() => {
    setLoading(true);
    setError(null);
    getEbookPages(id, currentChunk)
      .then((res) => {
        setPages(res.data.pages);
        setTotalPages(res.data.totalPages);
        setTotalChunks(res.data.totalChunks);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (err.response?.status === 403) {
          setError(t("ebookReader.noAccess"));
        }
        setLoading(false);
      });
  }, [id, currentChunk, t]);

  // Global page numbers
  const globalLeft = (currentChunk - 1) * PAGES_PER_CHUNK + currentPage + 1;
  const globalRight = isMobile ? globalLeft : Math.min(globalLeft + 1, totalPages);
  const progressPercent = totalPages > 0 ? (globalRight / totalPages) * 100 : 0;

  // Total spreads for bottom nav
  const totalSpreads = isMobile
    ? totalPages
    : Math.ceil(totalPages / 2);
  const currentSpread = isMobile
    ? globalLeft
    : Math.ceil(globalLeft / 2);

  // Navigation
  const goForward = useCallback(() => {
    if (isFlipping || loading) return;
    const step = isMobile ? 1 : 2;
    const nextPage = currentPage + step;

    if (nextPage >= pages.length) {
      // Need next chunk
      if (currentChunk >= totalChunks) return;
      pendingChunkRef.current = currentChunk + 1;
      pendingPageRef.current = 0;
    } else {
      pendingChunkRef.current = null;
      pendingPageRef.current = nextPage;
    }

    setFlipDirection("forward");
    setIsFlipping(true);
  }, [isFlipping, loading, isMobile, currentPage, pages.length, currentChunk, totalChunks]);

  const goBackward = useCallback(() => {
    if (isFlipping || loading) return;
    const step = isMobile ? 1 : 2;
    const prevPage = currentPage - step;

    if (prevPage < 0) {
      // Need previous chunk
      if (currentChunk <= 1) return;
      pendingChunkRef.current = currentChunk - 1;
      // Will go to end of previous chunk
      pendingPageRef.current = -1; // sentinel: means "last spread"
    } else {
      pendingChunkRef.current = null;
      pendingPageRef.current = prevPage;
    }

    setFlipDirection("backward");
    setIsFlipping(true);
  }, [isFlipping, loading, isMobile, currentPage, currentChunk]);

  const onFlipEnd = useCallback(() => {
    if (pendingChunkRef.current !== null) {
      const newChunk = pendingChunkRef.current;
      setCurrentChunk(newChunk);
      // If going backward to previous chunk, we need to wait for pages to load
      // then set currentPage to last spread. We handle this via a ref.
      if (pendingPageRef.current === -1) {
        // Will be resolved after chunk loads
        pendingPageRef.current = -1; // keep sentinel
      } else {
        setCurrentPage(pendingPageRef.current ?? 0);
      }
    } else if (pendingPageRef.current !== null) {
      setCurrentPage(pendingPageRef.current);
    }
    setIsFlipping(false);
  }, []);

  // When pages load and we have a pending "last spread" navigation
  useEffect(() => {
    if (pages.length > 0 && pendingPageRef.current === -1) {
      const lastPage = isMobile
        ? pages.length - 1
        : pages.length - 2 >= 0
          ? pages.length - (pages.length % 2 === 0 ? 2 : 1)
          : 0;
      setCurrentPage(lastPage);
      pendingPageRef.current = null;
    }
  }, [pages, isMobile]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goForward();
      if (e.key === "ArrowLeft") goBackward();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goForward, goBackward]);

  // Can navigate?
  const canGoForward =
    currentPage + (isMobile ? 1 : 2) < pages.length || currentChunk < totalChunks;
  const canGoBackward = currentPage > 0 || currentChunk > 1;

  // Current visible pages
  const leftPageUrl = pages[currentPage] ?? null;
  const rightPageUrl = !isMobile ? (pages[currentPage + 1] ?? null) : null;

  // Next/prev pages for flip overlay
  const nextLeftUrl = pages[currentPage + (isMobile ? 1 : 2)] ?? null;
  const nextRightUrl = !isMobile ? (pages[currentPage + 3] ?? null) : null;
  const prevRightUrl = pages[currentPage - 1] ?? null;
  const prevLeftUrl = !isMobile ? (pages[currentPage - 2] ?? null) : null;

  if (error) {
    return (
      <>
        <div className="bg-primary-800 -mt-20 h-20" />
        <div className="min-h-screen flex items-center justify-center bg-[#f5f0e8] text-neutral-800">
          <div className="bg-danger-50 border border-danger-200 text-danger-700 px-8 py-6 rounded-2xl shadow-lg">
            <span className="font-semibold text-lg">{error}</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="bg-primary-800 -mt-20 h-20" />
      <div
        className="min-h-screen bg-[#f5f0e8] text-neutral-800"
        onContextMenu={(e) => e.preventDefault()}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E")`,
        }}
      >
        {/* Top bar */}
        <div className="sticky top-20 z-40 bg-white/80 backdrop-blur-md border-b border-neutral-200/60">
          <div className="mx-auto max-w-5xl px-4 py-2.5 flex items-center justify-between">
            <p className="text-sm font-medium text-neutral-600">
              {t("ebookReader.page")}{" "}
              {isMobile
                ? `${globalLeft} / ${totalPages}`
                : `${globalLeft}-${globalRight} / ${totalPages}`}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                disabled={currentChunk <= 1}
                onClick={() => {
                  setCurrentChunk((c) => c - 1);
                  setCurrentPage(0);
                }}
                className="w-8 h-8 flex items-center justify-center text-sm font-medium rounded-lg text-neutral-600 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                ←
              </button>
              <span className="text-xs text-neutral-400 min-w-[3rem] text-center">
                {currentChunk} / {totalChunks}
              </span>
              <button
                disabled={currentChunk >= totalChunks}
                onClick={() => {
                  setCurrentChunk((c) => c + 1);
                  setCurrentPage(0);
                }}
                className="w-8 h-8 flex items-center justify-center text-sm font-medium rounded-lg text-neutral-600 hover:bg-neutral-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                →
              </button>
            </div>
          </div>
          {/* Progress bar */}
          <div className="h-[2px] bg-neutral-100">
            <div
              className="h-full bg-gradient-to-r from-warm-400 to-warm-600 transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="relative">
              <svg
                className="w-12 h-12 text-warm-400 animate-bounce-slow"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
              </svg>
            </div>
            <p className="text-sm text-neutral-400 font-medium">
              {t("ebookReader.page")}...
            </p>
          </div>
        )}

        {/* Book */}
        {!loading && pages.length > 0 && (
          <div className="mx-auto max-w-5xl px-4 py-8">
            {/* 3D Perspective wrapper */}
            <div style={{ perspective: "1500px" }}>
              {/* Book container */}
              <div
                className="relative mx-auto bg-white rounded-sm overflow-hidden select-none"
                style={{
                  boxShadow:
                    "0 10px 40px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)",
                  maxWidth: isMobile ? "500px" : "900px",
                }}
              >
                {isMobile ? (
                  /* ==================== MOBILE: Single page ==================== */
                  <div className="relative" style={{ transformStyle: "preserve-3d" }}>
                    {/* Click zones */}
                    <div
                      className="absolute inset-y-0 left-0 w-1/2 z-30 cursor-pointer"
                      onClick={goBackward}
                    />
                    <div
                      className="absolute inset-y-0 right-0 w-1/2 z-30 cursor-pointer"
                      onClick={goForward}
                    />

                    {/* Current page */}
                    <PageImage
                      src={leftPageUrl}
                      alt={`Page ${globalLeft}`}
                    />

                    {/* Forward flip overlay */}
                    {isFlipping && flipDirection === "forward" && (
                      <div
                        className="absolute inset-0 z-20 animate-page-flip-forward"
                        style={{
                          transformStyle: "preserve-3d",
                          transformOrigin: "left center",
                        }}
                        onAnimationEnd={onFlipEnd}
                      >
                        {/* Front: current page */}
                        <div
                          className="absolute inset-0"
                          style={{ backfaceVisibility: "hidden" }}
                        >
                          <PageImage src={leftPageUrl} alt="flip front" />
                        </div>
                        {/* Back: next page */}
                        <div
                          className="absolute inset-0"
                          style={{
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg)",
                          }}
                        >
                          <PageImage src={nextLeftUrl} alt="flip back" />
                        </div>
                      </div>
                    )}

                    {/* Backward flip overlay */}
                    {isFlipping && flipDirection === "backward" && (
                      <div
                        className="absolute inset-0 z-20 animate-page-flip-backward"
                        style={{
                          transformStyle: "preserve-3d",
                          transformOrigin: "right center",
                        }}
                        onAnimationEnd={onFlipEnd}
                      >
                        {/* Front: current page */}
                        <div
                          className="absolute inset-0"
                          style={{ backfaceVisibility: "hidden" }}
                        >
                          <PageImage src={leftPageUrl} alt="flip front" />
                        </div>
                        {/* Back: prev page */}
                        <div
                          className="absolute inset-0"
                          style={{
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg)",
                          }}
                        >
                          <PageImage src={prevRightUrl} alt="flip back" />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* ==================== DESKTOP: Two-page spread ==================== */
                  <div
                    className="relative flex"
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    {/* Click zones */}
                    <div
                      className="absolute inset-y-0 left-0 w-1/2 z-30 cursor-pointer"
                      onClick={goBackward}
                    />
                    <div
                      className="absolute inset-y-0 right-0 w-1/2 z-30 cursor-pointer"
                      onClick={goForward}
                    />

                    {/* Left page */}
                    <div className="relative w-1/2">
                      <PageImage
                        src={leftPageUrl}
                        alt={`Page ${globalLeft}`}
                      />
                      {/* Spine shadow on right edge */}
                      <div
                        className="absolute inset-y-0 right-0 w-6 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(to left, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                        }}
                      />
                    </div>

                    {/* Center spine line */}
                    <div
                      className="absolute inset-y-0 left-1/2 w-[2px] z-10 -translate-x-1/2 pointer-events-none"
                      style={{
                        background:
                          "linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.18), rgba(0,0,0,0.1))",
                      }}
                    />

                    {/* Right page */}
                    <div className="relative w-1/2">
                      <PageImage
                        src={rightPageUrl}
                        alt={`Page ${globalRight}`}
                      />
                      {/* Spine shadow on left edge */}
                      <div
                        className="absolute inset-y-0 left-0 w-6 pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(to right, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                        }}
                      />
                    </div>

                    {/* Forward flip overlay (right page flips) */}
                    {isFlipping && flipDirection === "forward" && (
                      <div
                        className="absolute inset-y-0 right-0 w-1/2 z-20 animate-page-flip-forward"
                        style={{
                          transformStyle: "preserve-3d",
                          transformOrigin: "left center",
                        }}
                        onAnimationEnd={onFlipEnd}
                      >
                        {/* Front: current right page */}
                        <div
                          className="absolute inset-0"
                          style={{ backfaceVisibility: "hidden" }}
                        >
                          <PageImage src={rightPageUrl} alt="flip front" />
                          {/* Spine shadow */}
                          <div
                            className="absolute inset-y-0 left-0 w-6 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(to right, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                            }}
                          />
                        </div>
                        {/* Back: new left page */}
                        <div
                          className="absolute inset-0"
                          style={{
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg)",
                          }}
                        >
                          <PageImage src={nextLeftUrl} alt="flip back" />
                          {/* Spine shadow on right edge (because it's flipped) */}
                          <div
                            className="absolute inset-y-0 right-0 w-6 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(to left, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Backward flip overlay (left page flips) */}
                    {isFlipping && flipDirection === "backward" && (
                      <div
                        className="absolute inset-y-0 left-0 w-1/2 z-20 animate-page-flip-backward"
                        style={{
                          transformStyle: "preserve-3d",
                          transformOrigin: "right center",
                        }}
                        onAnimationEnd={onFlipEnd}
                      >
                        {/* Front: current left page */}
                        <div
                          className="absolute inset-0"
                          style={{ backfaceVisibility: "hidden" }}
                        >
                          <PageImage src={leftPageUrl} alt="flip front" />
                          {/* Spine shadow on right edge */}
                          <div
                            className="absolute inset-y-0 right-0 w-6 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(to left, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                            }}
                          />
                        </div>
                        {/* Back: previous right page */}
                        <div
                          className="absolute inset-0"
                          style={{
                            backfaceVisibility: "hidden",
                            transform: "rotateY(180deg)",
                          }}
                        >
                          <PageImage src={prevRightUrl} alt="flip back" />
                          {/* Spine shadow on left edge */}
                          <div
                            className="absolute inset-y-0 left-0 w-6 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(to right, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Under-layer: next spread revealed after forward flip */}
                    {isFlipping && flipDirection === "forward" && (
                      <>
                        {/* New left page (revealed under the flipping right page) */}
                        <div className="absolute inset-y-0 left-0 w-1/2">
                          <PageImage src={nextLeftUrl} alt="next left" />
                          <div
                            className="absolute inset-y-0 right-0 w-6 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(to left, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                            }}
                          />
                        </div>
                        {/* New right page */}
                        <div className="absolute inset-y-0 right-0 w-1/2">
                          <PageImage src={nextRightUrl} alt="next right" />
                          <div
                            className="absolute inset-y-0 left-0 w-6 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(to right, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                            }}
                          />
                        </div>
                      </>
                    )}

                    {/* Under-layer: prev spread revealed after backward flip */}
                    {isFlipping && flipDirection === "backward" && (
                      <>
                        {/* Prev left page */}
                        <div className="absolute inset-y-0 left-0 w-1/2">
                          <PageImage src={prevLeftUrl} alt="prev left" />
                          <div
                            className="absolute inset-y-0 right-0 w-6 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(to left, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                            }}
                          />
                        </div>
                        {/* Prev right page */}
                        <div className="absolute inset-y-0 right-0 w-1/2">
                          <PageImage src={prevRightUrl} alt="prev right" />
                          <div
                            className="absolute inset-y-0 left-0 w-6 pointer-events-none"
                            style={{
                              background:
                                "linear-gradient(to right, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.04) 40%, transparent 100%)",
                            }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Bottom navigation */}
            <div className="flex justify-between items-center mt-6">
              <button
                disabled={!canGoBackward || isFlipping}
                onClick={goBackward}
                className="px-6 py-3 text-sm font-semibold rounded-xl bg-gradient-to-r from-warm-500 to-warm-600 text-white shadow-fun-warm hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:from-neutral-300 disabled:to-neutral-400 disabled:shadow-none transition-all duration-200"
              >
                ← {t("ebookReader.prev")}
              </button>
              <span className="text-sm font-medium text-neutral-500">
                {currentSpread} / {totalSpreads}
              </span>
              <button
                disabled={!canGoForward || isFlipping}
                onClick={goForward}
                className="px-6 py-3 text-sm font-semibold rounded-xl bg-gradient-to-r from-warm-500 to-warm-600 text-white shadow-fun-warm hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:from-neutral-300 disabled:to-neutral-400 disabled:shadow-none transition-all duration-200"
              >
                {t("ebookReader.next")} →
              </button>
            </div>
          </div>
        )}

        {/* Anti-copy CSS */}
        <style>{`
          img {
            -webkit-user-drag: none;
            -khtml-user-drag: none;
            -moz-user-drag: none;
            -o-user-drag: none;
          }
        `}</style>
      </div>
    </>
  );
};

/** Reusable page image with anti-copy protection */
const PageImage = ({ src, alt }: { src: string | null; alt: string }) => {
  if (!src) {
    return (
      <div className="w-full aspect-[3/4] bg-neutral-50 flex items-center justify-center">
        <span className="text-neutral-300 text-sm">—</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      draggable={false}
      className="w-full select-none pointer-events-none block"
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
      alt={alt}
    />
  );
};

export default EbookReader;
